/**
 * @file useBannerSlideTrack.js
 * @description Banner 轨道双向滑动：下一屏从右往左，上一屏从左往右
 * @module features/home/composables/useBannerSlideTrack
 */

import { nextTick, ref } from 'vue'
import {
  getBackwardSlideSteps,
  getBackwardLoopTrackIndex,
  getCircularNextIndex,
  getCircularPrevIndex,
  getForwardLoopTrackIndex,
  getForwardSlideSteps,
  resolveSlideDirection,
  toBannerTrackIndex,
} from '@/features/home/utils/bannerLink'

/** 与 CSS `--transition-base`（0.3s）对齐，略留余量作 transitionend 兜底 */
const TRANSITION_MS = 350

/**
 * 等待两帧，确保无 transition 的 transform 已绘制完成
 * @returns {Promise<void>}
 */
function waitPaintFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve)
    })
  })
}

/**
 * @param {object} options
 * @param {import('vue').Ref<HTMLElement|null>} options.trackRef
 * @param {import('vue').Ref<boolean>} options.transitionEnabled
 * @param {import('vue').Ref<number>} options.displayTrackIndex
 * @param {() => number} options.getSlideCount
 */
export function useBannerSlideTrack({
  trackRef,
  transitionEnabled,
  displayTrackIndex,
  getSlideCount,
}) {
  const displaySlideIndex = ref(0)
  let isAnimating = false
  /** @type {number|null} */
  let pendingTarget = null
  /** @type {'forward' | 'backward' | 'auto' | null} */
  let pendingDirection = null
  /** @type {ReturnType<typeof setTimeout>|null} */
  let transitionTimer = null
  /** @type {((event: TransitionEvent) => void)|null} */
  let transitionHandler = null

  function clearTransitionWait() {
    if (transitionTimer) {
      clearTimeout(transitionTimer)
      transitionTimer = null
    }
    if (transitionHandler && trackRef.value) {
      trackRef.value.removeEventListener('transitionend', transitionHandler)
    }
    transitionHandler = null
  }

  /**
   * @returns {Promise<void>}
   */
  function waitTrackTransition() {
    clearTransitionWait()
    return new Promise((resolve) => {
      const finish = () => {
        clearTransitionWait()
        resolve()
      }
      transitionHandler = (event) => {
        if (event.target !== trackRef.value || event.propertyName !== 'transform') {
          return
        }
        finish()
      }
      trackRef.value?.addEventListener('transitionend', transitionHandler)
      transitionTimer = setTimeout(finish, TRANSITION_MS)
    })
  }

  /**
   * 循环无缝跳转：动画到克隆屏后瞬间复位到真实屏。
   * 必须等 Vue 提交 transform 并完成绘制后，再恢复 transition，否则会看到反向“再滑一次”。
   * @param {number} animateTo
   * @param {number} snapTo
   */
  function runLoopSnap(animateTo, snapTo) {
    return new Promise((resolve) => {
      let finished = false

      transitionEnabled.value = true
      displayTrackIndex.value = animateTo

      const finish = () => {
        if (finished) return
        finished = true
        clearTransitionWait()

        const el = trackRef.value
        transitionEnabled.value = false
        // 内联禁用 transition，避免 class 切换与 Vue DOM 更新竞态
        if (el) el.style.transition = 'none'
        displayTrackIndex.value = snapTo

        nextTick()
          .then(() => {
            void el?.offsetWidth
            return waitPaintFrame()
          })
          .then(() => {
            if (el) el.style.transition = ''
            transitionEnabled.value = true
            resolve()
          })
      }

      transitionHandler = (event) => {
        if (event.target !== trackRef.value || event.propertyName !== 'transform') {
          return
        }
        finish()
      }
      trackRef.value?.addEventListener('transitionend', transitionHandler)
      transitionTimer = setTimeout(finish, TRANSITION_MS)
    })
  }

  /**
   * @param {number} from
   * @param {number} to
   */
  async function animateOneForwardStep(from, to) {
    const count = getSlideCount()
    if (count < 2) return

    if (from === count - 1 && to === 0) {
      // 循环中勿提前切换 displaySlideIndex，否则当前屏格子变 disabled 会视觉变暗
      await runLoopSnap(getForwardLoopTrackIndex(count), toBannerTrackIndex(0, count))
      displaySlideIndex.value = 0
      return
    }

    transitionEnabled.value = true
    displayTrackIndex.value = toBannerTrackIndex(to, count)
    displaySlideIndex.value = to
    await waitTrackTransition()
  }

  /**
   * @param {number} from
   * @param {number} to
   */
  async function animateOneBackwardStep(from, to) {
    const count = getSlideCount()
    if (count < 2) return

    if (from === 0 && to === count - 1) {
      await runLoopSnap(
        getBackwardLoopTrackIndex(),
        toBannerTrackIndex(count - 1, count)
      )
      displaySlideIndex.value = count - 1
      return
    }

    transitionEnabled.value = true
    displayTrackIndex.value = toBannerTrackIndex(to, count)
    displaySlideIndex.value = to
    await waitTrackTransition()
  }

  /**
   * @param {number} from
   * @param {number} to
   */
  async function animateForwardSteps(from, to) {
    const count = getSlideCount()
    const steps = getForwardSlideSteps(from, to, count)
    if (steps === 0) return

    let current = from
    for (let step = 0; step < steps; step++) {
      const next = getCircularNextIndex(count, current)
      await animateOneForwardStep(current, next)
      current = next
    }
  }

  /**
   * @param {number} from
   * @param {number} to
   */
  async function animateBackwardSteps(from, to) {
    const count = getSlideCount()
    const steps = getBackwardSlideSteps(from, to, count)
    if (steps === 0) return

    let current = from
    for (let step = 0; step < steps; step++) {
      const next = getCircularPrevIndex(count, current)
      await animateOneBackwardStep(current, next)
      current = next
    }
  }

  /**
   * @param {number} from
   * @param {number} to
   * @param {'forward' | 'backward' | 'auto'} direction
   */
  async function runAnimatedSlide(from, to, direction) {
    const count = getSlideCount()
    if (count < 2) return

    if (isAnimating) {
      pendingTarget = to
      pendingDirection = direction
      return
    }

    let resolved = direction
    if (resolved === 'auto') {
      resolved = resolveSlideDirection(from, to, count)
    }
    if (resolved === 'none') return

    isAnimating = true
    if (resolved === 'forward') {
      await animateForwardSteps(from, to)
    } else {
      await animateBackwardSteps(from, to)
    }
    isAnimating = false

    if (pendingTarget !== null && pendingTarget !== displaySlideIndex.value) {
      const target = pendingTarget
      const nextDirection = pendingDirection ?? 'auto'
      pendingTarget = null
      pendingDirection = null
      await runAnimatedSlide(displaySlideIndex.value, target, nextDirection)
    } else {
      pendingTarget = null
      pendingDirection = null
    }
  }

  /**
   * 强制从右往左切屏（下一屏 / 自动轮播）
   * @param {number} from
   * @param {number} to
   */
  async function animateForward(from, to) {
    await runAnimatedSlide(from, to, 'forward')
  }

  /**
   * 强制从左往右切屏（上一屏）
   * @param {number} from
   * @param {number} to
   */
  async function animateBackward(from, to) {
    await runAnimatedSlide(from, to, 'backward')
  }

  /**
   * 分页点：取最短路径
   * @param {number} from
   * @param {number} to
   */
  async function animateToSlide(from, to) {
    await runAnimatedSlide(from, to, 'auto')
  }

  /**
   * @param {number} activeIndex
   */
  function syncAtRest(activeIndex) {
    if (isAnimating) return
    const count = getSlideCount()
    displaySlideIndex.value = activeIndex
    displayTrackIndex.value = toBannerTrackIndex(activeIndex, count)
  }

  function cleanup() {
    clearTransitionWait()
    isAnimating = false
    pendingTarget = null
    pendingDirection = null
    const el = trackRef.value
    if (el) el.style.transition = ''
  }

  return {
    displaySlideIndex,
    animateToSlide,
    animateForward,
    animateBackward,
    syncAtRest,
    isAnimating: () => isAnimating,
    cleanup,
  }
}
